(() => {
  if (window.RecentlyViewed) return;

  const STORAGE_KEY = 'rs:recently-viewed:v1';
  const MAX_STORED = 20;
  const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

  const read = () => {
    try {
      const items = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (!Array.isArray(items)) return [];
      const cutoff = Date.now() - MAX_AGE_MS;
      return items.filter((item) => item
        && typeof item.id === 'string'
        && typeof item.context === 'string'
        && typeof item.html === 'string'
        && item.viewedAt > cutoff);
    } catch (e) {
      return [];
    }
  };

  const write = (items) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      return true;
    } catch (e) {
      return false;
    }
  };

  // Only blocks storage where a consent banner is shown and the visitor declined.
  const storageAllowed = () => {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (privacy && typeof privacy.preferencesProcessingAllowed === 'function') {
      return privacy.preferencesProcessingAllowed();
    }
    return true;
  };

  const record = (template) => {
    if (window.Shopify && window.Shopify.designMode) return;
    if (!storageAllowed()) return;

    const { productId, context } = template.dataset;
    const card = template.content.cloneNode(true);
    // Keep only the main and hover photos; full galleries make each saved card several times larger.
    card.querySelectorAll('.product-block__image--secondary:not(.product-block__image--show-on-hover)').forEach((el) => el.remove());
    const wrapper = document.createElement('div');
    wrapper.appendChild(card);
    const html = wrapper.innerHTML.trim();
    if (!productId || !html) return;

    const items = read().filter((item) => item.id !== productId);
    items.unshift({
      id: productId,
      context,
      html,
      viewedAt: Date.now()
    });
    items.length = Math.min(items.length, MAX_STORED);

    // Drop the oldest entries if storage is full.
    while (items.length && !write(items)) items.pop();
  };

  class RecentlyViewedProducts extends HTMLElement {
    connectedCallback() {
      if (this.rendered || this.observer) return;

      if (!('IntersectionObserver' in window)) {
        this.render();
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.stopObserving();
        this.render();
      }, { rootMargin: '0px 0px 600px 0px' });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.stopObserving();
    }

    stopObserving() {
      if (!this.observer) return;
      this.observer.disconnect();
      this.observer = null;
    }

    render() {
      this.rendered = true;

      const limit = parseInt(this.dataset.limit, 10) || 12;
      let cards = read()
        .filter((item) => item.context === this.dataset.context && item.id !== this.dataset.productId)
        .slice(0, limit)
        .map((item) => item.html);

      const usingPlaceholders = !cards.length;
      if (usingPlaceholders) {
        const placeholder = this.querySelector('template[data-recently-viewed-placeholder]');
        if (!placeholder) return;
        cards = Array(6).fill(placeholder.innerHTML);
      }

      const shell = this.querySelector('template[data-recently-viewed-shell]').content.cloneNode(true);
      const grid = shell.querySelector('.slider__grid');

      cards.forEach((html, index) => {
        const item = document.createElement('div');
        item.className = 'slider__item';
        item.innerHTML = html;
        if (!usingPlaceholders) RecentlyViewedProducts.addTrackingParams(item, index + 1);
        grid.appendChild(item);
      });

      this.matchAspectRatios(grid);

      // The slider reads its slides when it's inserted, so they must already be in place.
      this.appendChild(shell);
    }

    static addTrackingParams(item, position) {
      item.querySelectorAll('a[href*="/products/"]').forEach((link) => {
        const url = new URL(link.getAttribute('href'), window.location.origin);
        url.searchParams.set('rec', 'rv');
        url.searchParams.set('rec_pos', position);
        link.setAttribute('href', url.pathname + url.search + url.hash);
      });
    }

    // Cards are saved one at a time, so the shared image shape is chosen here (as collection grids do).
    matchAspectRatios(grid) {
      const mode = this.dataset.aspectMode;
      if (mode !== 'widest' && mode !== 'tallest') return;

      const frames = [...grid.querySelectorAll('.image-cont .img-ar')];
      const ratios = frames
        .map((frame) => parseFloat(frame.style.getPropertyValue('--aspect-ratio')))
        .filter((ratio) => ratio > 0);
      if (!ratios.length) return;

      const target = mode === 'tallest' ? Math.min(...ratios) : Math.max(...ratios);
      frames.forEach((frame) => frame.style.setProperty('--aspect-ratio', target));
    }
  }

  window.RecentlyViewed = { read, record };

  if (!customElements.get('recently-viewed-products')) {
    customElements.define('recently-viewed-products', RecentlyViewedProducts);
  }

  document.querySelectorAll('template[data-recently-viewed-record]').forEach(record);
})();
